import { isPlatformBrowser, registerLocaleData } from '@angular/common'
import { provideHttpClient, withInterceptors } from '@angular/common/http'
import localeNl from '@angular/common/locales/nl'
import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  PLATFORM_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core'
import {
  provideClientHydration,
  withNoIncrementalHydration,
} from '@angular/platform-browser'
import { Router, TitleStrategy } from '@angular/router'

import {
  provideMissingTranslationHandler,
  provideTranslateLoader,
  provideTranslateService,
} from '@ngx-translate/core'
import * as Sentry from '@sentry/angular'
import type { IncludeBearerTokenCondition } from 'keycloak-angular'
import {
  AutoRefreshTokenService,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  UserActivityService,
  createInterceptorCondition,
  includeBearerTokenInterceptor,
  withAutoRefreshToken,
} from 'keycloak-angular'
import { NgPipesModule } from 'ngx-pipes'
import type { Observable } from 'rxjs'
import { EMPTY, from, switchMap, take } from 'rxjs'
import { catchError, finalize, map } from 'rxjs/operators'

import { environment } from '../environments/environment'
import { GraphQLModule } from './api/graphql.module'
import { httpFailureLoggingInterceptor } from './api/http-failure-logging.interceptor'
import { AppRoutingModule } from './app-routing.module'
import { ComponentsModule } from './components/components.module'
import { FeaturesModule } from './features/features.module'
import { JsonTranslateLoader } from './misc/json.translate-loader'
import { MyMissingTranslationHandler } from './misc/missing-translations'
import { AuthService } from './services/auth/auth.service'
import { DateParserService } from './services/date-service/date-parser.service'
import { InternationalizationService } from './services/internationalization.service'
import { SearchService } from './services/search/search.service'
import { provideKeycloakSSR } from './shared/providers/keycloak.provider'
import { LocaleAwareRouter } from './shared/providers/locale-aware-router'
import { SeoTitleStrategy } from './shared/seo/seo-title.strategy'

// Registreer de taaldata
registerLocaleData(localeNl)

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function createApiUrlPattern(apiUrl: string): RegExp {
  const normalized = apiUrl.replace(/\/+$/, '')
  return new RegExp(`^${escapeRegExp(normalized)}(?:/|$)`, 'i')
}

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: createApiUrlPattern(environment.apiUrl),
  bearerPrefix: 'Bearer',
})

export function authInitializer(
  authService: AuthService,
  platformId: object
): () => Observable<void> {
  return () =>
    authService.initKeycloak().pipe(
      switchMap(authenticated => {
        if (!authenticated && isPlatformBrowser(platformId)) {
          const idpHint = new URLSearchParams(window.location.search).get(
            'idp_hint'
          )
          const provider = environment.keycloak.providers.find(
            provider => provider.idpHint === idpHint && provider.enabled
          )
          if (idpHint && provider) {
            return from(
              authService.loginWithKeycloak(idpHint, window.location.pathname)
            )
          }
        }
        return authService.initializeUserFromToken()
      }),
      take(1),
      map(() => void 0),
      catchError((err: unknown) => {
        console.warn(err)
        authService.captureError(err, 'check_failed')
        return EMPTY
      }),
      finalize(() => {
        authService.markAuthCheckCompleted()
      })
    )
}

export function translationsInitializer(
  internationalizationService: InternationalizationService
): () => void {
  return () => {
    internationalizationService.init()
  }
}

// De standalone configuratie die AppModule vervangt
export const appConfig: ApplicationConfig = {
  providers: [
    // Oude NgModule imports die services leveren converteren we met importProvidersFrom
    importProvidersFrom(
      AppRoutingModule,
      FeaturesModule,
      ComponentsModule,
      GraphQLModule,
      NgPipesModule
    ),
    provideTranslateService({
      loader: provideTranslateLoader(JsonTranslateLoader),
      fallbackLang: environment.id === 'production' ? 'nl' : undefined,
      missingTranslationHandler: provideMissingTranslationHandler(
        MyMissingTranslationHandler
      ),
    }),

    // Keycloak & HTTP
    provideKeycloakSSR({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'none',
        }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition],
    },
    provideClientHydration(withNoIncrementalHydration()),
    provideHttpClient(
      withInterceptors([
        includeBearerTokenInterceptor,
        httpFailureLoggingInterceptor,
      ])
    ),
    // Overrides the Router provided by AppRoutingModule above so absolute
    // navigations (routerLink, router.navigate) keep the /en prefix.
    { provide: Router, useClass: LocaleAwareRouter },
    // Drives the whole document head (title, description, Open Graph, Twitter
    // card, canonical) off the active route on every navigation, SSR included.
    { provide: TitleStrategy, useClass: SeoTitleStrategy },

    // Initializers
    provideAppInitializer(() => {
      const initializerFn = authInitializer(
        inject(AuthService),
        inject(PLATFORM_ID)
      )
      return initializerFn()
    }),
    provideAppInitializer(() => {
      const initializerFn = translationsInitializer(
        inject(InternationalizationService)
      )
      initializerFn()
    }),
    { provide: LOCALE_ID, useValue: 'nl' },
    SearchService,
    DateParserService,
    {
      provide: ErrorHandler,
      useFactory: () => Sentry.createErrorHandler(),
    },
    Sentry.TraceService,
  ],
}
